import React, { useMemo, useState } from 'react';
import { AlertTriangle, GitMerge, Unlink, X } from 'lucide-react';

import { useResolveVendorDuplicateCandidate, useVendorDuplicateCandidate } from '@api/hooks/useVendors';
import Button from '@components/common/Button';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import type { VendorDuplicateCandidate, VendorSearchResponse } from '@schemas/api';
import { formatVendorTaxIds } from '@/lib/vendorTaxIds';

interface VendorDuplicateResolutionModalProps {
  candidate: VendorDuplicateCandidate | null;
  onClose: () => void;
}

const compareFields: Array<{ key: keyof VendorSearchResponse; label: string; render?: (vendor: VendorSearchResponse) => string }> = [
  { key: 'legal_name', label: 'Legal name' },
  { key: 'company_name', label: 'Company' },
  { key: 'print_on_check_name', label: 'Print name' },
  { key: 'tax_ids', label: 'Tax IDs', render: (vendor) => formatVendorTaxIds(vendor.tax_ids) },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
  { key: 'default_payment_terms', label: 'Terms' },
  { key: 'currency_preference', label: 'Currency' },
  { key: 'status', label: 'Status' },
];

function fieldValue(vendor: VendorSearchResponse, field: typeof compareFields[number]): string {
  if (field.render) return field.render(vendor) || '-';
  const value = vendor[field.key];
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function evidenceList(evidence: Record<string, unknown>, key: 'reasons' | 'conflicts'): string[] {
  const topMatch = evidence.top_match;
  if (!topMatch || typeof topMatch !== 'object') return [];
  const values = (topMatch as Record<string, unknown>)[key];
  return Array.isArray(values) ? values.map(String) : [];
}

export const VendorDuplicateResolutionModal: React.FC<VendorDuplicateResolutionModalProps> = ({
  candidate,
  onClose,
}) => {
  const [selectedPrimaryId, setSelectedPrimaryId] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');
  const { data: detail, isLoading } = useVendorDuplicateCandidate(candidate?.id);
  const resolveCandidate = useResolveVendorDuplicateCandidate();
  const activeCandidate = detail || candidate;

  const proposedPrimaryId = activeCandidate?.proposed_primary_vendor_id || activeCandidate?.vendors[0]?.id || '';
  const primaryId = selectedPrimaryId || proposedPrimaryId;
  const reasons = useMemo(() => evidenceList(activeCandidate?.evidence || {}, 'reasons'), [activeCandidate]);
  const conflicts = useMemo(() => evidenceList(activeCandidate?.evidence || {}, 'conflicts'), [activeCandidate]);

  if (!candidate) return null;

  const handleCombine = async () => {
    if (!activeCandidate || !primaryId) return;
    await resolveCandidate.mutateAsync({
      candidateId: activeCandidate.id,
      payload: {
        action: 'combine',
        primary_vendor_id: primaryId,
        resolution_note: resolutionNote || undefined,
      },
    });
    onClose();
  };

  const handleSeparate = async () => {
    if (!activeCandidate) return;
    await resolveCandidate.mutateAsync({
      candidateId: activeCandidate.id,
      payload: {
        action: 'separate',
        resolution_note: resolutionNote || undefined,
      },
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-start justify-between border-b border-default p-5 dark:border-default-dark">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-content-primary dark:text-content-primary-dark">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Resolve possible duplicate vendors
            </h2>
            <p className="mt-1 text-sm text-content-secondary">
              Confidence {Math.round((activeCandidate?.confidence_score || 0) * 100)}%
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-content-muted hover:bg-raised hover:text-content-primary"
            aria-label="Close duplicate resolution"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {isLoading || !activeCandidate ? (
            <LoadingSpinner text="Loading duplicate candidate..." />
          ) : (
            <div className="space-y-5">
              {(reasons.length > 0 || conflicts.length > 0) && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                    <div className="font-medium">Matched evidence</div>
                    <ul className="mt-2 list-disc space-y-1 pl-4">
                      {(reasons.length ? reasons : ['Name similarity matched']).map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <div className="font-medium">Uncertainty</div>
                    <ul className="mt-2 list-disc space-y-1 pl-4">
                      {(conflicts.length ? conflicts : ['Admin review required before combining']).map((conflict) => (
                        <li key={conflict}>{conflict}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto rounded border border-default dark:border-default-dark">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-raised dark:bg-raised-dark">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-content-primary">Field</th>
                      {activeCandidate.vendors.map((vendor) => (
                        <th key={vendor.id} className="px-4 py-3 text-left font-semibold text-content-primary">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="primary-vendor"
                              value={vendor.id}
                              checked={primaryId === vendor.id}
                              onChange={() => setSelectedPrimaryId(vendor.id)}
                            />
                            {vendor.legal_name}
                          </label>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {compareFields.map((field) => (
                      <tr key={field.key} className="border-t border-default dark:border-default-dark">
                        <td className="px-4 py-3 font-medium text-content-secondary">{field.label}</td>
                        {activeCandidate.vendors.map((vendor) => (
                          <td key={`${vendor.id}-${field.key}`} className="px-4 py-3 text-content-primary">
                            {fieldValue(vendor, field)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="border-t border-default dark:border-default-dark">
                      <td className="px-4 py-3 font-medium text-content-secondary">ERP links</td>
                      {activeCandidate.vendors.map((vendor) => {
                        const links = detail?.erp_links_by_vendor_id[vendor.id] || vendor.erp_links || [];
                        return (
                          <td key={`${vendor.id}-erp-links`} className="px-4 py-3 text-content-primary">
                            {links.length ? (
                              <div className="space-y-1">
                                {links.map((link) => (
                                  <div key={link.id} className="rounded border border-default px-2 py-1 text-xs">
                                    <div className="font-medium">{link.connection_label || `${link.erp_type} ${link.realm_id}`}</div>
                                    <div className="text-content-muted">{link.external_vendor_id} - {link.status}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <label className="block text-sm font-medium text-content-secondary">Resolution note</label>
                <textarea
                  value={resolutionNote}
                  onChange={(event) => setResolutionNote(event.target.value)}
                  className="input mt-1 min-h-[80px] w-full"
                  placeholder="Optional note for audit history"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-default p-5 dark:border-default-dark">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="secondary" onClick={handleSeparate} loading={resolveCandidate.isPending} disabled={!activeCandidate}>
            <span className="inline-flex items-center gap-2">
              <Unlink className="h-4 w-4" />
              Keep Separate
            </span>
          </Button>
          <Button variant="primary" onClick={handleCombine} loading={resolveCandidate.isPending} disabled={!activeCandidate || !primaryId}>
            <span className="inline-flex items-center gap-2">
              <GitMerge className="h-4 w-4" />
              Combine
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
